/**
 * The frame every donor screen sits in.
 *
 * Header on top, scrolling body in the middle, Contact Us band at the bottom.
 * This exists so the sixteen phone screens cannot drift apart: none of them
 * lays out its own header or footer, and the support details are fetched once
 * and shared.
 *
 * A screen supplies only its body, plus an optional pinned footer action for
 * the checkout screens whose CTA stays reachable above the fold.
 */

import { memo, type ReactNode } from 'react';
import {
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { colors, sizing, space } from '../../theme';
import { useAppConfig } from '../../core/api/hooks';
import { ContactBar } from './ContactBar';
import { Header, type HeaderProps } from './Header';
import { ScreenTitle } from './ScreenTitle';
import { useLanguage } from '../../core/i18n/LanguageProvider';

export interface ScreenShellProps extends Omit<HeaderProps, 'testID'> {
  children: ReactNode;
  /** Sticky action area above the impact band, e.g. "Proceed to Payment". */
  pinnedFooter?: ReactNode;
  /**
   * A band under the header that does not scroll away.
   *
   * Takes the page background, not the header's: white here made the chrome a
   * tall slab, where every screen shows a compact bar and then the page.
   */
  subHeader?: ReactNode;
  /**
   * The screen's title, rendered in the same green card Home's hero uses.
   *
   * A prop rather than something each screen lays out, so every screen's
   * title is the same card in the same place — they had drifted into a dozen
   * bare headings on the page background.
   */
  title?: string;
  subtitle?: string;
  /** Disables the scroll view for screens that manage their own list. */
  scrollable?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
  contentStyle?: StyleProp<ViewStyle>;
  /** Removes the body's horizontal padding for edge-to-edge content. */
  flush?: boolean;
  /**
   * Replaces the contact band at the bottom of the screen.
   *
   * Pass `null` for a screen that wants no footer at all.
   */
  footer?: ReactNode;
  /**
   * Told the height the body has to fill — the scrolling area, less its own
   * top and bottom padding — whenever that changes. Home uses it to fit
   * itself to a tall phone without scrolling.
   */
  onBodySpace?: (height: number) => void;
  testID?: string;
}

export const ScreenShell = memo(function ScreenShell({
  children,
  pinnedFooter,
  subHeader,
  title,
  subtitle,
  scrollable = true,
  onRefresh,
  refreshing = false,
  contentStyle,
  flush = false,
  footer,
  onBodySpace,
  testID,
  ...headerProps
}: ScreenShellProps) {
  // Every screen closes with the same Contact Us band. It used to be the
  // impact statistics, which said the same four numbers on all sixteen screens
  // and gave a donor nothing to act on; the figures still open Impact, in
  // full. One cached query, shared, so the band costs one request per session.
  const { data: config } = useAppConfig();
  const { isRTL } = useLanguage();
  // On a short window (a phone turned sideways) the title card and the CTA
  // scroll with the page rather than pinning, or they leave the content a
  // strip between them. Screens that manage their own list keep them pinned:
  // there is no scroll view here to put them in. In a mobile browser the soft
  // keyboard shortens the window too; the switch then gives the field being
  // typed in the room, and the body keeps its slot, so focus and text survive.
  const { height } = useWindowDimensions();
  const short = scrollable && height < sizing.shortWindowHeight;

  const body = (
    <View style={[!flush && styles.gutter, styles.body, contentStyle]}>{children}</View>
  );

  const titleBand =
    subHeader || title ? (
      <View style={styles.subHeader}>
        <View style={[styles.column, styles.columnAuto]}>
          {subHeader ?? <ScreenTitle title={title ?? ''} subtitle={subtitle} />}
        </View>
      </View>
    ) : null;

  const pinned = pinnedFooter ? (
    <View style={styles.pinned}>
      <View style={[styles.column, styles.columnAuto]}>{pinnedFooter}</View>
    </View>
  ) : null;

  return (
    <SafeAreaView
      style={[styles.safe, { direction: isRTL ? 'rtl' : 'ltr' }]}
      // The contact band keeps itself clear of the bottom system bar; a screen
      // that replaces it gets the bottom edge from here instead.
      edges={footer === undefined ? ['top'] : ['top', 'bottom']}
      testID={testID}
      {...webDirection(isRTL)}
    >
      <Header {...headerProps} />

      {short ? null : titleBand}

      {scrollable ? (
        <ScrollView
          style={styles.scroll}
          onLayout={
            onBodySpace
              ? (event) =>
                  onBodySpace(
                    event.nativeEvent.layout.height -
                      styles.body.paddingTop -
                      (pinnedFooter ? 0 : styles.scrollContent.paddingBottom),
                  )
              : undefined
          }
          // A pinned footer already separates the body from what follows, so
          // the bottom gutter is dead height there — enough of it, on a short
          // phone, to make a screen that fits scroll anyway.
          contentContainerStyle={[
            styles.scrollContent,
            pinnedFooter ? styles.scrollContentPinned : null,
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          refreshControl={
            onRefresh ? (
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                tintColor={colors.text.brand}
              />
            ) : undefined
          }
        >
          {short ? titleBand : null}
          <View style={styles.column}>{body}</View>
          {short ? pinned : null}
        </ScrollView>
      ) : (
        <View style={styles.scroll}>
          <View style={styles.column}>{body}</View>
        </View>
      )}

      {short ? null : pinned}

      {footer === undefined ? <ContactBar support={config?.support} /> : footer}
    </SafeAreaView>
  );
});

/**
 * The web build resolves `start`/`end` — and margins and paddings by start and
 * end — from a `dir` attribute on an ancestor, not from the CSS direction set
 * above, so without it every one of them stayed left-to-right in Urdu. Native
 * mirrors from the app's own direction and needs nothing.
 */
function webDirection(isRTL: boolean): object {
  return Platform.OS === 'web' ? { dir: isRTL ? 'rtl' : 'ltr' } : {};
}

const styles = StyleSheet.create({
  /**
   * One centred column, capped at phone width. Everything inside — including
   * the pinned CTA — inherits it, so nothing stretches across a desktop
   * browser. On a phone the cap is never reached and this is a no-op.
   */
  column: {
    width: '100%',
    maxWidth: sizing.donorMaxWidth,
    alignSelf: 'center',
    flex: 1,
  },
  /**
   * The pinned footer measures itself from its content, so its column must
   * not flex. `flex: 1` is `flexBasis: 0` plus grow, and in a parent whose
   * own height comes from its children there is no free space to grow into:
   * the column resolves to zero and the CTA collapses to a sliver of padding
   * behind the band below it. On Android that made "Continue to Checkout"
   * unreachable and the donation impossible to complete — a real device found
   * it; the web build and the whole suite did not.
   */
  columnAuto: {
    flex: 0,
  },
  safe: {
    flex: 1,
    backgroundColor: colors.bg.page,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: space.xl,
  },
  scrollContentPinned: {
    paddingBottom: 0,
  },
  body: {
    flex: 1,
    paddingTop: space.lg,
  },
  gutter: {
    paddingHorizontal: space.lg,
  },
  subHeader: {
    backgroundColor: colors.bg.page,
    paddingHorizontal: space.lg,
    paddingTop: space.md,
    paddingBottom: space.sm,
  },
  pinned: {
    paddingHorizontal: space.lg,
    paddingTop: space.md,
    paddingBottom: space.md,
    backgroundColor: colors.bg.page,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
  },
});
